import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO } from "../../lib/site";
import { trackEvent } from "../../lib/analytics";
import { PrimaryButton, OutlineButton } from "./Buttons";
import { MemberLoginAction } from "./MemberLogin";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled || open
          ? "bg-[#020A12]/90 backdrop-blur-md shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
          : "bg-gradient-to-b from-[#020A12]/80 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
          <img src={LOGO} alt="MST — Multiple Stream Team" className="h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" style={{ fontFamily: "var(--font-heading)" }}>
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] font-bold uppercase tracking-widest transition-colors duration-300 ${
                  isActive ? "text-[#00C4F4]" : "text-white/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <MemberLoginAction
            render={(p) => (
              <OutlineButton {...p} data-testid="header-member-login-btn" className="!px-5 !py-2.5 !text-xs">
                Member Login
              </OutlineButton>
            )}
          />
          <PrimaryButton to="/apply" data-testid="header-apply-btn" onClick={() => trackEvent("apply_membership_click", { location: "header" })} className="!px-5 !py-2.5 !text-xs">
            Apply for Membership
          </PrimaryButton>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#020A12]/95 backdrop-blur-md border-t border-white/5"
          >
            <div className="px-6 py-8 flex flex-col gap-7" style={{ fontFamily: "var(--font-heading)" }}>
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `text-sm font-bold uppercase tracking-widest py-1 ${isActive ? "text-[#00C4F4]" : "text-white/90"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex flex-col gap-4 pt-5 border-t border-white/10">
                <MemberLoginAction
                  render={(p) => (
                    <OutlineButton {...p} data-testid="mobile-member-login-btn">Member Login</OutlineButton>
                  )}
                />
                <PrimaryButton to="/apply" data-testid="mobile-apply-btn" onClick={() => trackEvent("apply_membership_click", { location: "mobile_menu" })}>Apply for Membership</PrimaryButton>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
